import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
    ArrowLeft,
    MapPin,
    User,
    Calendar,
    FileText,
    IndianRupee,
    Clock,
    CheckCircle,
    AlertCircle,
    Loader2,
    Download,
    Eye,
    Building2,
    Mail,
    Phone,
    Home
} from 'lucide-react';
import api from '../services/api';
import { QuotationType } from '../constants/stageConstants';
import { format } from 'date-fns';

/**
 * ProjectDetailView - Stage-based project details view with lazy-loaded tabs
 * 
 * API Endpoints Required:
 * =====================
 * 
 * 1. GET /api/projects/:id/overview
 *    Returns: { project: {...}, entities: [...], oem: {...} }
 *    - Basic project info (ClientName, Location, Sqft, Status, StageKey, CreatedAt, etc.)
 *    - Entities array (Entity name, Quantity, etc.)
 *    - OEM details if assigned
 * 
 * 2. GET /api/projects/:id/quotations
 *    Returns: { quotations: [...] }
 *    - Array of quotations for this project
 *    - Each quotation: { Id, QuotationNumber, QuotationType, Status, TotalAmount, GrandTotal, LineItems (JSONB), CreatedAt, PreparedBy, etc. }
 * 
 * 3. GET /api/projects/:id/documents
 *    Returns: { documents: [...] }
 *    - Array of documents uploaded for project
 *    - Each document: { Id, DocumentType, FileName, FileUrl, UploadedBy, UploadedAt, FileSize, etc. }
 * 
 * 4. GET /api/projects/:id/history
 *    Returns: { timeline: [...] }
 *    - Project audit log / timeline events
 *    - Each event: { Id, Action, Description, PerformedBy, PerformedAt, OldValue, NewValue, etc. }
 *    - Examples: "Project created", "Quotation shared", "Status changed from X to Y", etc.
 */

const ProjectDetailView = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    // Main project header data (loaded initially)
    const [loading, setLoading] = useState(true);
    const [project, setProject] = useState(null);
    const [error, setError] = useState(null);

    // Tab management
    const [activeTab, setActiveTab] = useState('overview');

    // Tab-specific data and loading states
    const [overviewData, setOverviewData] = useState(null);
    const [overviewLoading, setOverviewLoading] = useState(false);

    const [quotations, setQuotations] = useState([]);
    const [quotationsLoading, setQuotationsLoading] = useState(false);

    const [documents, setDocuments] = useState([]);
    const [documentsLoading, setDocumentsLoading] = useState(false);

    const [history, setHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    // Load initial project header data
    useEffect(() => {
        fetchProjectHeader();
    }, [id]);

    // Load tab data when tab changes
    useEffect(() => {
        if (!project) return;

        switch (activeTab) {
            case 'overview':
                if (!overviewData) fetchOverview();
                break;
            case 'quotation':
                if (quotations.length === 0) fetchQuotations();
                break;
            case 'document':
                if (documents.length === 0) fetchDocuments();
                break;
            case 'history':
                if (history.length === 0) fetchHistory();
                break;
            default:
                break;
        }
    }, [activeTab, project]);

    // Fetch minimal project header data for display
    const fetchProjectHeader = async () => {
        try {
            setLoading(true);
            setError(null);

            // API returns data directly, not wrapped in response.data
            const projectData = await api.get(`/api/projects/${id}`);

            console.log('Project Data:', projectData);

            setProject({
                id: projectData.id || id,
                clientName: projectData.clientName,
                location: projectData.siteAddress || projectData.cityName || 'N/A',
                sqft: projectData.sqft,
                status: projectData.stageName || 'New Project',
                stageKey: projectData.stageId,
                createdAt: projectData.createdAt
            });

            setLoading(false);
        } catch (error) {
            console.error('Error fetching project header:', error);
            console.error('Error details:', error.message);
            setError(error.message || 'Failed to load project');
            setLoading(false);
        }
    };

    // API: GET /api/projects/:id/overview
    const fetchOverview = async () => {
        try {
            setOverviewLoading(true);

            const data = await api.get(`/api/projects/${id}/overview`);
            setOverviewData(data);

            setOverviewLoading(false);
        } catch (error) {
            console.error('Error fetching overview:', error);
            setOverviewLoading(false);
        }
    };

    // API: GET /api/projects/:id/quotations
    const fetchQuotations = async () => {
        try {
            setQuotationsLoading(true);

            const data = await api.get(`/api/projects/${id}/quotations`);
            setQuotations(data.quotations || []);

            setQuotationsLoading(false);
        } catch (error) {
            console.error('Error fetching quotations:', error);
            setQuotationsLoading(false);
        }
    };

    // API: GET /api/projects/:id/documents
    const fetchDocuments = async () => {
        try {
            setDocumentsLoading(true);

            const data = await api.get(`/api/projects/${id}/documents`);
            setDocuments(data.documents || []);

            setDocumentsLoading(false);
        } catch (error) {
            console.error('Error fetching documents:', error);
            setDocumentsLoading(false);
        }
    };

    // API: GET /api/projects/:id/history
    const fetchHistory = async () => {
        try {
            setHistoryLoading(true);

            const data = await api.get(`/api/projects/${id}/history`);
            setHistory(data.timeline || []);

            setHistoryLoading(false);
        } catch (error) {
            console.error('Error fetching history:', error);
            setHistoryLoading(false);
        }
    };

    // Format file size
    const formatFileSize = (bytes) => {
        if (!bytes) return 'N/A';
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(amount);
    };

    // Get status badge color
    const getStatusBadgeColor = (status) => {
        const statusColors = {
            'draft': 'bg-gray-100 text-gray-800 border-gray-300',
            'shared_with_oem': 'bg-blue-100 text-blue-800 border-blue-300',
            'approved': 'bg-green-100 text-green-800 border-green-300',
            'rejected': 'bg-red-100 text-red-800 border-red-300'
        };
        return statusColors[status] || 'bg-gray-100 text-gray-800';
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <Loader2 className="h-12 w-12 animate-spin text-teal-600 mx-auto mb-4" />
                    <p className="text-gray-600">Loading project details...</p>
                </div>
            </div>
        );
    }

    if (!project && !loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <Card className="w-full max-w-md">
                    <CardContent className="pt-6 text-center">
                        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold mb-2">
                            {error ? 'Error Loading Project' : 'Project Not Found'}
                        </h3>
                        <p className="text-gray-600 mb-4">
                            {error || "The project you're looking for doesn't exist."}
                        </p>
                        <p className="text-xs text-gray-500 mb-4">
                            Project ID: {id}
                        </p>
                        <Button onClick={() => navigate('/admin/dashboard')}>
                            Back to Dashboard
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-6">
                    <Button
                        variant="ghost"
                        onClick={() => navigate('/admin/dashboard')}
                        className="mb-4"
                    >
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Back to Dashboard
                    </Button>

                    <div className="flex items-start justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">{project.clientName}</h1>
                            <div className="flex items-center gap-4 mt-2 text-gray-600">
                                <div className="flex items-center gap-1">
                                    <MapPin className="h-4 w-4" />
                                    <span>{project.location}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <Home className="h-4 w-4" />
                                    <span>{project.sqft} sq.ft</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <Calendar className="h-4 w-4" />
                                    <span>{format(new Date(project.createdAt), 'MMM dd, yyyy')}</span>
                                </div>
                            </div>
                        </div>
                        <Badge variant="outline" className="text-lg px-4 py-2">
                            {project.status}
                        </Badge>
                    </div>
                </div>

                <Separator className="my-6" />

                {/* Tabs - Only 4 tabs for New Project stage */}
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 h-auto sm:h-10">
                        <TabsTrigger value="overview">Overview</TabsTrigger>
                        <TabsTrigger value="quotation">Quotation</TabsTrigger>
                        <TabsTrigger value="document">Document</TabsTrigger>
                        <TabsTrigger value="history">History</TabsTrigger>
                    </TabsList>

                    {/* Overview Tab */}
                    <TabsContent value="overview" className="space-y-6 mt-6">
                        {overviewLoading ? (
                            <Card>
                                <CardContent className="flex items-center justify-center py-12">
                                    <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
                                </CardContent>
                            </Card>
                        ) : overviewData ? (
                            <>
                                {/* Project Details Card */}
                                <Card>
                                    <CardHeader>
                                        <CardTitle>Project Information</CardTitle>
                                        <CardDescription>Basic project details and specifications</CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <div>
                                                <label className="text-sm font-medium text-gray-500">Client Name</label>
                                                <p className="text-base font-semibold mt-1">{overviewData.ClientName}</p>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-500">Location</label>
                                                <p className="text-base font-semibold mt-1">{overviewData.SiteAddress || overviewData.CityName}</p>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-500">Square Feet</label>
                                                <p className="text-base font-semibold mt-1">{overviewData.Sqft} sq.ft</p>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-500">Current Stage</label>
                                                <p className="text-base font-semibold mt-1">{overviewData.CurrentStage}</p>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-500">Workflow</label>
                                                <p className="text-base font-semibold mt-1">{overviewData.WorkflowName}</p>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-500">Progress</label>
                                                <p className="text-base font-semibold mt-1">{overviewData.OverallProgress}%</p>
                                            </div>
                                            {overviewData.Pricing && overviewData.Pricing.TotalAmount && (
                                                <div>
                                                    <label className="text-sm font-medium text-gray-500">Total Amount</label>
                                                    <p className="text-base font-semibold mt-1">{formatCurrency(overviewData.Pricing.TotalAmount || 0)}</p>
                                                </div>
                                            )}
                                            {overviewData.Pricing && overviewData.Pricing.BalanceAmount && (
                                                <div>
                                                    <label className="text-sm font-medium text-gray-500">Balance</label>
                                                    <p className="text-base font-semibold mt-1">{formatCurrency(overviewData.Pricing.BalanceAmount || 0)}</p>
                                                </div>
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>

                                {/* Entities Card */}
                                <Card>
                                    <CardHeader>
                                        <CardTitle>Entities</CardTitle>
                                        <CardDescription>Project entities and specifications</CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        {overviewData.Entities && overviewData.Entities.length > 0 ? (
                                            <div className="overflow-x-auto">
                                                <Table className="min-w-[600px]">
                                                    <TableHeader>
                                                        <TableRow>
                                                            <TableHead className="whitespace-nowrap">Entity</TableHead>
                                                            <TableHead className="whitespace-nowrap">Quantity</TableHead>
                                                            <TableHead className="whitespace-nowrap">Status</TableHead>
                                                            <TableHead className="whitespace-nowrap">Progress</TableHead>
                                                        </TableRow>
                                                    </TableHeader>
                                                    <TableBody>
                                                        {overviewData.Entities.map((entity, index) => (
                                                            <TableRow key={index}>
                                                                <TableCell className="font-medium">{entity.EntityName}</TableCell>
                                                                <TableCell>{entity.Quantity}</TableCell>
                                                                <TableCell>
                                                                    <Badge variant={
                                                                        entity.Status === 'Completed' ? 'success' :
                                                                            entity.Status === 'In Progress' ? 'default' :
                                                                                'secondary'
                                                                    }>
                                                                        {entity.Status}
                                                                    </Badge>
                                                                </TableCell>
                                                                <TableCell>{entity.ProgressPercentage}%</TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </div>
                                        ) : (
                                            <p className="text-gray-500 text-sm">No entities added yet</p>
                                        )}
                                    </CardContent>
                                </Card>

                                {/* OEM Details Card */}
                                {overviewData.OemName && (
                                    <Card>
                                        <CardHeader>
                                            <CardTitle>OEM Details</CardTitle>
                                            <CardDescription>Assigned OEM information</CardDescription>
                                        </CardHeader>
                                        <CardContent>
                                            <div className="flex items-center gap-3">
                                                <Building2 className="h-5 w-5 text-gray-400" />
                                                <div>
                                                    <label className="text-sm font-medium text-gray-500">Company Name</label>
                                                    <p className="text-base font-semibold">{overviewData.OemName}</p>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                )}
                            </>
                        ) : null}
                    </TabsContent>

                    {/* Quotation Tab */}
                    <TabsContent value="quotation" className="space-y-6 mt-6">
                        {quotationsLoading ? (
                            <Card>
                                <CardContent className="flex items-center justify-center py-12">
                                    <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
                                </CardContent>
                            </Card>
                        ) : (
                            <Card>
                                <CardHeader>
                                    <CardTitle>Quotations</CardTitle>
                                    <CardDescription>All quotations for this project</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    {quotations.length > 0 ? (
                                        <div className="space-y-4">
                                            {quotations.map((quotation) => (
                                                <Card key={quotation.Id} className="border-2">
                                                    <CardContent className="pt-6">
                                                        <div className="flex items-start justify-between mb-4">
                                                            <div>
                                                                <h4 className="text-lg font-semibold">{quotation.QuotationNumber}</h4>
                                                                <p className="text-sm text-gray-500">
                                                                    Prepared on {format(new Date(quotation.CreatedAt), 'MMM dd, yyyy')} by {quotation.PreparedBy}
                                                                </p>
                                                            </div>
                                                            <Badge className={getStatusBadgeColor(quotation.Status)}>
                                                                {quotation.Status.replace('_', ' ').toUpperCase()}
                                                            </Badge>
                                                        </div>

                                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                                                            <div>
                                                                <label className="text-sm font-medium text-gray-500">Type</label>
                                                                <p className="text-base font-semibold mt-1">
                                                                    {quotation.QuotationType === QuotationType.BaseWork ? 'Base Work' : 'Additional Work'}
                                                                </p>
                                                            </div>
                                                            <div>
                                                                <label className="text-sm font-medium text-gray-500">Total Amount</label>
                                                                <p className="text-base font-semibold mt-1">{formatCurrency(quotation.TotalAmount)}</p>
                                                            </div>
                                                            <div>
                                                                <label className="text-sm font-medium text-gray-500">Grand Total (incl. Tax)</label>
                                                                <p className="text-base font-semibold mt-1 text-teal-600">{formatCurrency(quotation.GrandTotal)}</p>
                                                            </div>
                                                        </div>

                                                        <div className="flex gap-2 mt-4">
                                                            <Button size="sm" variant="outline">
                                                                <Eye className="h-4 w-4 mr-1" />
                                                                View Details
                                                            </Button>
                                                            <Button size="sm" variant="outline">
                                                                <Download className="h-4 w-4 mr-1" />
                                                                Download PDF
                                                            </Button>
                                                        </div>
                                                    </CardContent>
                                                </Card>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-12">
                                            <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                                            <p className="text-gray-500">No quotations created yet</p>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        )}
                    </TabsContent>

                    {/* Document Tab */}
                    <TabsContent value="document" className="space-y-6 mt-6">
                        {documentsLoading ? (
                            <Card>
                                <CardContent className="flex items-center justify-center py-12">
                                    <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
                                </CardContent>
                            </Card>
                        ) : (
                            <Card>
                                <CardHeader>
                                    <CardTitle>Documents</CardTitle>
                                    <CardDescription>All uploaded documents for this project</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    {documents.length > 0 ? (
                                        <div className="overflow-x-auto">
                                            <Table className="min-w-[800px]">
                                                <TableHeader>
                                                    <TableRow>
                                                        <TableHead className="whitespace-nowrap">Document Type</TableHead>
                                                        <TableHead className="whitespace-nowrap">File Name</TableHead>
                                                        <TableHead className="whitespace-nowrap">Size</TableHead>
                                                        <TableHead className="whitespace-nowrap">Uploaded By</TableHead>
                                                        <TableHead className="whitespace-nowrap">Uploaded At</TableHead>
                                                        <TableHead className="whitespace-nowrap">Actions</TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {documents.map((doc) => (
                                                        <TableRow key={doc.Id}>
                                                            <TableCell className="font-medium">{doc.DocumentType}</TableCell>
                                                            <TableCell>
                                                                <div className="flex items-center gap-2">
                                                                    <FileText className="h-4 w-4 text-blue-600" />
                                                                    {doc.FileName}
                                                                </div>
                                                            </TableCell>
                                                            <TableCell>{formatFileSize(doc.FileSize)}</TableCell>
                                                            <TableCell>{doc.UploadedBy}</TableCell>
                                                            <TableCell>{format(new Date(doc.UploadedAt), 'MMM dd, yyyy')}</TableCell>
                                                            <TableCell>
                                                                <div className="flex gap-2">
                                                                    <Button size="sm" variant="ghost">
                                                                        <Eye className="h-4 w-4" />
                                                                    </Button>
                                                                    <Button size="sm" variant="ghost">
                                                                        <Download className="h-4 w-4" />
                                                                    </Button>
                                                                </div>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </div>
                                    ) : (
                                        <div className="text-center py-12">
                                            <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                                            <p className="text-gray-500">No documents uploaded yet</p>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        )}
                    </TabsContent>

                    {/* History Tab */}
                    <TabsContent value="history" className="space-y-6 mt-6">
                        {historyLoading ? (
                            <Card>
                                <CardContent className="flex items-center justify-center py-12">
                                    <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
                                </CardContent>
                            </Card>
                        ) : (
                            <Card>
                                <CardHeader>
                                    <CardTitle>Project Timeline</CardTitle>
                                    <CardDescription>Activity history and status changes</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    {history.length > 0 ? (
                                        <div className="space-y-4">
                                            {history.map((event, index) => (
                                                <div key={event.Id} className="flex gap-4">
                                                    <div className="flex flex-col items-center">
                                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${event.Color === 'green' ? 'bg-green-100' :
                                                            event.Color === 'blue' ? 'bg-blue-100' :
                                                                event.Color === 'yellow' ? 'bg-yellow-100' :
                                                                    'bg-gray-100'
                                                            }`}>
                                                            {event.Icon === 'CheckCircle' ? <CheckCircle className="h-5 w-5 text-green-600" /> :
                                                                event.Icon === 'User' ? <User className="h-5 w-5 text-blue-600" /> :
                                                                    <Clock className="h-5 w-5 text-gray-600" />}
                                                        </div>
                                                        {index < history.length - 1 && (
                                                            <div className="w-0.5 h-full bg-gray-200 mt-2"></div>
                                                        )}
                                                    </div>
                                                    <div className="flex-1 pb-8">
                                                        <h4 className="font-semibold">{event.Action}</h4>
                                                        <p className="text-sm text-gray-600 mt-1">{event.Description}</p>
                                                        <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                                                            <span>{event.PerformedBy}</span>
                                                            <span>•</span>
                                                            <span>{format(new Date(event.PerformedAt), 'MMM dd, yyyy HH:mm')}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-12">
                                            <Clock className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                                            <p className="text-gray-500">No activity history yet</p>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        )}
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
};

export default ProjectDetailView;
